import { ConditionalProp, Content, ticketInfoType, getConditionalProp } from 'common/apiService';
import create from 'zustand';
import { getTimerDuration } from './commonUtils';
import { inspectFlags } from './contentUtils';
import getAxiosError from './getAxiosError';
import { ReactionType } from 'common/Reaction';

export const useConditionalProps = create<ConditionalPropsStore>((set, get) => ({
  loading: false,
  error: null,
  bookmarked: false,
  reaction: 'none',
  playable: false,
  free: false,
  hasAccess: true,
  contentId: '',
  firstEpisodeId: undefined,
  seriesId: undefined,
  ageRestricted: false,
  setPublicProps: ({ flags, contentId, firstEpisodeId, seriesId, ticket_info, serverTime, contentLongId }) => {
    const { playable, free, hasAccess, hasSpecificCarrie } = inspectFlags(flags);
    let ticket: CinemaTicket | undefined;
    const price = ticket_info?.price;
    const price_button_text = ticket_info?.price_button_text;
    if (price !== undefined) {
      const href = `/tickets/${firstEpisodeId || contentId}`;
      ticket = { price, href, price_button_text, ...ticket_info };
    }
    set({
      playable,
      free,
      contentId,
      firstEpisodeId,
      seriesId,
      ticket,
      serverTime,
      hasAccess,
      contentLongId,
      hasSpecificCarrie,
    });
  },
  fetch: async () => {
    set({ loading: true, error: null });

    const contentId = get().contentId;
    // const seriesId = get().seriesId;
    const firstEpisodeId = get().firstEpisodeId ?? '';
    const ticket = get().ticket;

    try {
      const {
        data: props,
        meta: { server_date_time },
      } = await getConditionalProp(contentId || firstEpisodeId);
      const { conditional_flag: flag, seasons, ticket_info,
        carrier_video_content_button_text: carrierBtnText, carrier_name_text, carrier_key } = props;
      const { bookmarked, playable, hasAccess, hasSpecificCarrie, ageRestricted } = inspectFlags(flag);
      const reaction = getReaction(flag);

      // eslint-disable-next-line max-len
      const showtime = getTimerDuration(ticket_info?.activation_date_time, server_date_time) || getTimerDuration(ticket_info?.expire_date_time, server_date_time);
      const newTicket = ticket && {
        ...ticket,
        price: ticket_info?.price || ticket.price,
        ...ticket_info,
      };
      set({
        loading: false,
        reaction,
        bookmarked,
        playable,
        seasons,
        showtime,
        ticket: newTicket,
        hasAccess,
        hasSpecificCarrie,
        carrierBtnText,
        carrier_name_text,
        carrier_key,
        ageRestricted,
      });

      // if (seriesId) {
      //   const { data: series  } = await getConditionalProp(seriesId);

      //   const { bookmarked } = inspectFlags(series.conditional_flag);
      //   set({ bookmarked });
      // }
    } catch (e) {
      set({
        loading: false,
        error: getAxiosError(e),
      });
    }
  },
  updateBookmarkWithoutRequest: (bookmarked: boolean | undefined) => {
    set({
      bookmarked,
    });
  },
  updateMainContentReaction: (reaction: ReactionType) => {
    set({
      reaction,
    });
  },
}));

const getReaction = (flag: string): ConditionalProp['reaction'] => {
  if (flag.includes('dislike')) return 'dislike';
  if (flag.includes('like')) return 'like';
  return 'none';
};

export type ConditionalPropsStore = ConditionalProp &
  IDs & {
    hasAccess: boolean;
    free: boolean;
    ticket?: CinemaTicket;
    showtime?: number;
    loading: boolean;
    error?: string | null;
    serverTime?: string | undefined;
    hasSpecificCarrie?: boolean;
    fetch: () => void;
    setPublicProps: (props: IDs & { flags: string; ticket_info: Content['ticket_info'], serverTime?: string }) => void;
    updateBookmarkWithoutRequest: (bookmark: boolean | undefined) => void,
    updateMainContentReaction: (reaction: ReactionType) => void,
    carrierBtnText?: string,
    carrier_name_text?: string,
    carrier_key?: string,
    ageRestricted: boolean,
  };

export type CinemaTicket = ticketInfoType & {
  href: string;
};

type IDs = {
  contentId: string;
  firstEpisodeId?: string;
  seriesId?: string;
  contentLongId?: string
};

export default useConditionalProps;
